import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const PracticeContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PracticeProvider = ({ children }) => {
  const [practice, setPractice] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchPractice();
    }
  }, [user]);

  const fetchPractice = async () => {
    try {
      const response = await axios.get(`${API}/practice`);
      setPractice(response.data);
    } catch (error) {
      console.error('Failed to fetch practice:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateThresholds = async (warningThreshold, criticalThreshold) => {
    try {
      await axios.put(`${API}/practice/thresholds`, null, {
        params: {
          warning_threshold: warningThreshold,
          critical_threshold: criticalThreshold
        }
      });
      await fetchPractice();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to update thresholds' };
    }
  };

  const updateAppearance = async (backgroundColor, backgroundImage, backgroundImageDark) => {
    try {
      await axios.put(`${API}/practice/appearance`, {
        background_color: backgroundColor,
        background_image: backgroundImage || null,
        background_image_dark: backgroundImageDark || null
      });
      await fetchPractice();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to update appearance' };
    }
  };

  const updatePracticeName = async (name) => {
    try {
      await axios.put(`${API}/practice/name`, { name });
      await fetchPractice();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to update practice name' };
    }
  };

  const updateDisplaySettings = async (settings) => {
    try {
      await axios.put(`${API}/practice/display-settings`, settings);
      await fetchPractice();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to update display settings' };
    }
  };

  const updateSoundSettings = async (settings) => {
    try {
      await axios.put(`${API}/practice/sound-settings`, settings);
      await fetchPractice();
      return { success: true };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Failed to update sound settings' };
    }
  };

  return (
    <PracticeContext.Provider value={{ practice, loading, updateThresholds, updateAppearance, updatePracticeName, updateDisplaySettings, updateSoundSettings, fetchPractice }}>
      {children}
    </PracticeContext.Provider>
  );
};

export const usePractice = () => {
  const context = useContext(PracticeContext);
  if (!context) {
    throw new Error('usePractice must be used within PracticeProvider');
  }
  return context;
};
